import React, { ReactNode, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from '../../../../theme';
import {
  Box,
  CardContent,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Collapse,
  Autocomplete,
  InputAdornment,
  Paper,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { CREATE_CREDIT_TIERS_RESULTS, GET_SCORECARD } from '../queries';
import {
  ApplicantData,
  Conditions,
  ScoreBand,
  ScoreBreakdownItem,
  ScorecardEngine,
  ScoreResult,
} from '@trnsact/trnsact-scorecard-sdk';
import { FACT_NAMES_BOOLEAN, FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import _ from 'lodash';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { useMutation, useQuery } from '@apollo/client';

import { ContentViewType, SelectOption } from 'global';
import { ScorecardCriteria } from '../types';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import { CreditApplication, LenderWaterfallPrescreenCriteriaResultType } from '@trnsact/trnsact-shared-types';
import Tile from 'components/Tile/Tile';
import { formatCurrency } from 'utils';
import { autocompletePropsByType } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/constants';
import { determineRuleType } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/lib';
import { ConditionsFieldsTypes } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/types';
interface Props {
  vo: any;
  refetchVO: any;
  vp: any;
  account: any;
  equipmentData: any;
  refetchEquipment: any;
  equipmentLoading: any;
  userProfile: any;
  refetchCreditApp: any;
  creditApplication: CreditApplication;
  portalConfigurationDataLoading: any;
  creditAppOwners: any[];
  cs: any;
  refetchCreditSubmission?: any;
  contentViewType?: ContentViewType;
}

const getFicoScoreFromCbrs = (cbrs: any[]): number => {
  try {
    const ficoReport = cbrs.find(cbr => {
      return cbr && cbr.bureauType === 'FICO';
    });
    if (ficoReport) {
      console.info(`Found FICO Report`);
      const ficoField = ficoReport.fields?.find((f: any) => {
        return f?.key === 'FICO Score';
      });
      if (ficoField && ficoField.value) {
        return parseInt(ficoField.value, 10);
      }
    }
    console.warn('Could not find FICO score... returning score of 0 (not available)');
    return 0;
  } catch (e: any) {
    console.error(`Error getting FICO Score... returning score of 0: ${e.message}`);
    return 0;
  }
};
export const ScoreCardV2 = ({
  vo,
  refetchVO,
  refetchCreditSubmission,
  vp,
  account,
  equipmentData,
  refetchEquipment,
  equipmentLoading,
  refetchCreditApp,
  cs,
  creditApplication,
  portalConfigurationDataLoading,
  contentViewType = ContentViewType.Desktop,
  creditAppOwners,
}: Props) => {
  const APPLICANT_OPEN_STATE_KEY: string = 'applicant';
  const [createCreditTiersResults, { data, loading, error }] = useMutation(CREATE_CREDIT_TIERS_RESULTS);

  const handleCreateResults = async (inputArray: any) => {
    try {
      const response = await createCreditTiersResults({ variables: { input: inputArray } });
      // handle response.data.createCreditTiersResults as needed
    } catch (err) {
      // handle error
      console.log(error);
      debugger;
    }
  };
  const classes = useStyles();
  const history = useHistory();
  const [scorecardCriteria, setScorecardCriteria] = useState<ScorecardCriteria | null>(null);
  const [financeProgramId, setFinanceProgramId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalScore, setTotalScore] = useState<number>(0);
  const [band, setBand] = useState<string>('');
  const [bandColor, setBandColor] = useState<string>('#FFF');
  const [breakDown, setBreakDown] = useState<ScoreBreakdownItem[]>([]);
  const [engine, setEngine] = useState<ScorecardEngine | null>(null);
  const [results, setResults] = useState<
    | (ScoreResult & {
        band?: string;
        bandColor?: string;
      })
    | null
  >(null);
  const [applicantData, setApplicantData] = useState<ApplicantData | null>(null);
  const [listOfFacts, setListOfFacts] = useState<
    Map<
      string,
      {
        fact: string;
        type: ConditionsFieldsTypes;
        factType: FACT_NAMES_BOOLEAN | FACT_NAMES_NUMERIC | FACT_NAMES_STRING;
      }
    >
  >(new Map());
  // Store available string values for each string fact
  const [stringFactOptions, setStringFactOptions] = useState<Map<string, SelectOption[]>>(new Map());
  // Manually-entered fields state
  const [manualFields, setManualFields] = useState<{ [key: string]: any }>({});
  // Only allow editing fields that are part of ApplicantData
  // Define the allowed keys and their labels
  const applicantFieldDefs = React.useMemo<{ key: keyof ApplicantData; label: string }[]>(
    () => [
      // Numeric fields (from FACT_NAMES_NUMERIC)
      { key: 'DEAL_SIZE', label: 'Deal Size' },
      { key: 'TIME_IN_BUSINESS_MONTHS', label: 'Time in Business (Months)' },
      { key: 'FICO', label: 'FICO Score' },
      { key: 'NUMBER_OF_TRUCKS', label: 'Number of Trucks' },
      { key: 'EQUIPMENT_YEAR', label: 'Equipment Year' },
      { key: 'EQUIPMENT_AGE_YEARS', label: 'Equipment Age (Years)' },
      { key: 'EQUIPMENT_HOURS', label: 'Equipment Hours' },
      { key: 'DOWN_PAYMENT', label: 'Down Payment' },
      { key: 'TIME_WITH_CDL_MONTHS', label: 'Time with CDL (Months)' },
      { key: 'TIME_SINCE_BANKRUPTCY_MONTHS', label: 'Time Since Bankruptcy (Months)' },
      { key: 'ODOMETER_MILEAGE', label: 'Odometer Mileage' },
      { key: 'COVERAGE_TERM_LENGTH_MONTHS', label: 'Coverage Term Length (Months)' },
      { key: 'IN_SERVICE_DATE', label: 'In Service Date' },
      { key: 'YEARS_IN_BUSINESS', label: 'Years in Business' },
      { key: 'BANK_BALANCE_PER_TRUCK', label: 'Bank Balance Per Truck' },
      { key: 'PERSONAL_CREDIT_SCORE', label: 'Personal Credit Score' },
      { key: 'INDUSTRY_EXPERIENCE_YEARS', label: 'Industry Experience Years' },
      { key: 'AVERAGE_BANK_BALANCE', label: 'Average Bank Balance' },
      { key: 'FLEET_SIZE', label: 'Fleet Size' },

      // String fields (from FACT_NAMES_STRING)
      { key: 'EQUIPMENT_TYPE', label: 'Equipment Type' },
      { key: 'EQUIPMENT_MAKE', label: 'Equipment Make' },
      { key: 'EQUIPMENT_MODEL', label: 'Equipment Model' },
      { key: 'EQUIPMENT_CLASS', label: 'Equipment Class' },
      { key: 'FINANCING_TYPE', label: 'Financing Type' },
      { key: 'LEGAL_STRUCTURE', label: 'Legal Structure' },
      { key: 'EQUIPMENT_CONDITION', label: 'Equipment Condition' },
      { key: 'USA_STATE', label: 'USA State' },
      { key: 'CANADA_REGION', label: 'Canada Region' },
      { key: 'VIN', label: 'VIN' },
      { key: 'DEDUCTIBLE', label: 'Deductible' },
      { key: 'COVERAGE_TERM', label: 'Coverage Term' },
      { key: 'CONTRACT_TERM', label: 'Contract Term' },
      { key: 'BILLING_FREQUENCY', label: 'Billing Frequency' },
      { key: 'AFTERMARKET_PRODUCT_SOURCE', label: 'Aftermarket Product Source' },
      { key: 'LOCATION_ID', label: 'Location ID' },
      { key: 'AFTERMARKET_PRODUCT_ID', label: 'Aftermarket Product ID' },
      { key: 'AFTERMARKET_PRODUCT_ID_EXTERNAL', label: 'Aftermarket Product ID External' },
      { key: 'AFTERMARKET_PRODUCT_IDS_EXTERNAL_QUALIFIED', label: 'Aftermarket Product IDs External Qualified' },
      { key: 'TERM_AND_MILEAGE', label: 'Term and Mileage' },
      { key: 'EQUIPMENT_ENGINE_TYPE', label: 'Equipment Engine Type' },
      { key: 'ENGINE_TORQUE_MODE', label: 'Engine Torque Mode' },
      { key: 'DEALERS', label: 'Dealers' },
      { key: 'COMPARABLE_LOAN_HISTORY', label: 'Comparable Loan History' },
      { key: 'DRIVER_STRUCTURE', label: 'Driver Structure' },
      { key: 'HOME_OWNERSHIP', label: 'Home Ownership' },

      // Boolean fields (from FACT_NAMES_BOOLEAN)
      { key: 'OWNER_OPERATOR', label: 'Owner Operator' },
      { key: 'LONG_HAUL', label: 'Long Haul' },
      { key: 'SOLE_PROPRIETORSHIP', label: 'Sole Proprietorship' },
      { key: 'FIRST_TIME_BUYER', label: 'First Time Buyer' },
      { key: 'CORPORATION_ONLY', label: 'Corporation Only' },
      { key: 'PRIOR_FINANCING_EXPERIENCE', label: 'Prior Financing Experience' },
      { key: 'US_CITIZEN', label: 'US Citizen' },
      { key: 'EQUIPMENT_HAS_ATS', label: 'Equipment Has ATS' },
      { key: 'IS_REPEAT_BORROWER', label: 'Is Repeat Borrower' },

      // Date fields (from FACT_NAMES_DATE)
      { key: 'DATE_VALID', label: 'Date Valid' },
    ],
    []
  );

  // Only show fields that are in ApplicantData or have been manually entered
  const allApplicantKeys = React.useMemo(() => {
    const keys = applicantFieldDefs.map(f => f.key);
    // If a user has entered a value for a field not in the default list, include it
    Object.keys(manualFields).forEach(k => {
      if (!keys.includes(k as keyof ApplicantData)) keys.push(k as keyof ApplicantData);
    });
    return keys;
  }, [manualFields, applicantFieldDefs]);
  const { data: scorecardData, refetch: refetchScorecard } = useQuery(GET_SCORECARD, {
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      setScorecardCriteria(data.scoreCard[0]?.prescreenCriteria);

      setFinanceProgramId(data.scoreCard[0]?.financeProgramId);
      setIsLoading(false);
    },
  });

  const currentUserProfile = useSelector((state: any) => state.userProfile);

  useEffect(() => {
    setIsLoading(!creditApplication || !vo);
  }, [creditApplication, vo, portalConfigurationDataLoading]);

  useEffect(() => {
    if (!isLoading && scorecardCriteria) {
      let ficoScore = _.get(creditApplication, 'creditApplicationOwner[0].personalGuarantor.mostRecentCbrs', undefined);
      if (!_.isEmpty(ficoScore)) {
        ficoScore = getFicoScoreFromCbrs(ficoScore);
      }

      let eqModelYear = undefined;
      let eqAge = undefined;
      const currentYear = new Date().getFullYear();

      if (!_.isEmpty(_.get(equipmentData, 'equipments[0].year'))) {
        eqModelYear = Number(_.get(equipmentData, 'equipments[0].year'));
        eqAge = currentYear - eqModelYear;
      }

      // Map credit application fields to ApplicantData criteria
      // Extract equipment data
      const equipment = _.get(equipmentData, 'equipments[0]', {});

      // Calculate time fields
      const timeInBusinessMonths = creditApplication.yearsInBusiness
        ? creditApplication.yearsInBusiness * 12
        : undefined;
      const timeWithCdlMonths = _.get(creditApplication, 'primaryContact.yearsWithCdl', undefined)
        ? _.get(creditApplication, 'primaryContact.yearsWithCdl') * 12
        : undefined;
      const bankruptcyDate = _.get(creditApplication, 'primaryContact.bankruptcyDate', undefined);
      const timeSinceBankruptcyMonths = bankruptcyDate
        ? Math.floor((new Date().getTime() - new Date(bankruptcyDate).getTime()) / (1000 * 60 * 60 * 24 * 30))
        : undefined;

      let applicant: ApplicantData = {
        // === NUMERIC FIELDS (from FACT_NAMES_NUMERIC) ===
        DEAL_SIZE: creditApplication.amountRequested,
        TIME_IN_BUSINESS_MONTHS: timeInBusinessMonths,
        FICO: ficoScore,
        NUMBER_OF_TRUCKS: creditApplication.piecesOfTruckEqpt || creditApplication.trucksOwned,
        EQUIPMENT_YEAR: eqModelYear,
        EQUIPMENT_AGE_YEARS: eqAge,
        EQUIPMENT_HOURS: equipment.hours,
        DOWN_PAYMENT: vo?.downPayment,
        TIME_WITH_CDL_MONTHS: timeWithCdlMonths,
        TIME_SINCE_BANKRUPTCY_MONTHS: timeSinceBankruptcyMonths,
        ODOMETER_MILEAGE: equipment.mileage,
        COVERAGE_TERM_LENGTH_MONTHS: undefined, // Aftermarket specific
        IN_SERVICE_DATE: undefined, // Aftermarket specific
        YEARS_IN_BUSINESS: creditApplication.yearsInBusiness,
        BANK_BALANCE_PER_TRUCK: undefined, // Calculated below
        PERSONAL_CREDIT_SCORE: ficoScore,
        INDUSTRY_EXPERIENCE_YEARS: creditApplication.industryExpYears,
        AVERAGE_BANK_BALANCE:
          _.get(creditApplication, 'primaryBankAverageBalance', undefined) ||
          _.get(creditApplication, 'averageDailyBalance', undefined),
        FLEET_SIZE: creditApplication.piecesOfTruckEqpt || creditApplication.trucksOwned,

        // === STRING FIELDS (from FACT_NAMES_STRING) ===
        EQUIPMENT_TYPE: creditApplication.equipmentCategoryCode,
        EQUIPMENT_MAKE: equipment.make,
        EQUIPMENT_MODEL: equipment.model,
        EQUIPMENT_CLASS: equipment.classType,
        FINANCING_TYPE: vo?.financingType,
        LEGAL_STRUCTURE: creditApplication.legalStructure,
        EQUIPMENT_CONDITION: equipment.newUsed,
        USA_STATE: creditApplication.state,
        CANADA_REGION: creditApplication.state, // Use same as USA_STATE for Canada
        VIN: equipment.serialNumberVin,
        DEDUCTIBLE: undefined, // Aftermarket specific
        COVERAGE_TERM: undefined, // Aftermarket specific
        CONTRACT_TERM: undefined, // Aftermarket specific
        BILLING_FREQUENCY: undefined, // Aftermarket specific
        AFTERMARKET_PRODUCT_SOURCE: undefined,
        LOCATION_ID: creditApplication.location?.locationId,
        AFTERMARKET_PRODUCT_ID: undefined,
        AFTERMARKET_PRODUCT_ID_EXTERNAL: undefined,
        AFTERMARKET_PRODUCT_IDS_EXTERNAL_QUALIFIED: undefined,
        TERM_AND_MILEAGE: undefined,
        EQUIPMENT_ENGINE_TYPE: undefined,
        ENGINE_TORQUE_MODE: undefined,
        DEALERS: undefined,
        COMPARABLE_LOAN_HISTORY: undefined,
        DRIVER_STRUCTURE: creditApplication.numOfOwnerOps ? 'OWNER_OPERATOR' : undefined,
        HOME_OWNERSHIP: _.get(creditApplication, 'primaryContact.homeType', undefined),

        // === BOOLEAN FIELDS (from FACT_NAMES_BOOLEAN) ===
        OWNER_OPERATOR: creditApplication.numOfOwnerOps && creditApplication.numOfOwnerOps > 0,
        LONG_HAUL: creditApplication.longHaulRegional === 'Long Haul',
        SOLE_PROPRIETORSHIP: creditApplication.legalStructure === 'SOLE_PROPRIETORSHIP',
        FIRST_TIME_BUYER: _.get(creditApplication, 'primaryContact.firstTimeFinancing', false),
        CORPORATION_ONLY: creditApplication.legalStructure === 'CORPORATION',
        PRIOR_FINANCING_EXPERIENCE: creditApplication.previousFinanceExp === 'true',
        US_CITIZEN: _.get(creditApplication, 'primaryContact.usCitizen', undefined),
        EQUIPMENT_HAS_ATS: undefined, // Equipment specific attribute
        IS_REPEAT_BORROWER: creditApplication.previousFinanceExp === 'true',

        // === DATE FIELDS (from FACT_NAMES_DATE) ===
        DATE_VALID: creditApplication.dateEstablished || creditApplication.dateIncorporated,
      };

      // Calculate BANK_BALANCE_PER_TRUCK if we have both values
      if (applicant.AVERAGE_BANK_BALANCE && applicant.NUMBER_OF_TRUCKS && applicant.NUMBER_OF_TRUCKS > 0) {
        applicant.BANK_BALANCE_PER_TRUCK = applicant.AVERAGE_BANK_BALANCE / applicant.NUMBER_OF_TRUCKS;
      }

      // If there's existing scorecard result data, use it as base (allows manual overrides)
      if (cs && cs.scoreCardResult) {
        applicant = cs.scoreCardResult.runtimeInput;
      } else {
        if (vo && vo.scoreCardResult) {
          applicant = vo.scoreCardResult.runtimeInput;
        }
      }

      // Add FICO score if available
      if (!_.isEmpty(ficoScore)) {
        applicant = { ...applicant, PERSONAL_CREDIT_SCORE: ficoScore };
      }

      // Convert manualFields values to their correct types
      const parsedManualFields: { [key: string]: any } = {};
      Object.entries(manualFields).forEach(([k, v]) => {
        if (typeof v === 'string') {
          // Try to parse as number
          if (v.trim() !== '' && !isNaN(Number(v))) {
            parsedManualFields[k] = Number(v);
          } else if (v === 'true' || v === 'false') {
            parsedManualFields[k] = v === 'true';
          } else {
            parsedManualFields[k] = v;
          }
        } else {
          parsedManualFields[k] = v;
        }
      });

      // Merge parsed manual fields into applicant
      applicant = { ...applicant, ...parsedManualFields };

      // Use a Map to ensure unique entries per 'fact', using determineRuleType for proper type detection
      const factsMap = new Map();
      // Track all string values for each string fact
      const stringOptionsMap = new Map<string, Set<string>>();

      scorecardCriteria.jsonCriteria.scoreCriteria.forEach((scoreCardRow: any) => {
        scoreCardRow?.jsonRules.conditions.all.forEach((cond: any) => {
          const factName = cond.fact;
          const fieldType = determineRuleType(factName);
          factsMap.set(factName, { fact: factName, type: fieldType, factType: factName });

          // Collect string values from conditions
          if (fieldType === ConditionsFieldsTypes.String && cond.value) {
            if (!stringOptionsMap.has(factName)) {
              stringOptionsMap.set(factName, new Set());
            }
            // Handle both single values and arrays
            const values = Array.isArray(cond.value) ? cond.value : [cond.value];
            values.forEach((v: any) => {
              if (v && typeof v === 'string') {
                stringOptionsMap.get(factName)!.add(v);
              }
            });
          }
        });
        if (scoreCardRow && scoreCardRow?.customCriteria) {
          Object.values(scoreCardRow?.customCriteria).forEach((criteria: any) => {
            // For custom criteria, we need to use the criteria.type
            let fieldType: ConditionsFieldsTypes;
            switch (criteria.type) {
              case 'boolean':
                fieldType = ConditionsFieldsTypes.Boolean;
                break;
              case 'number':
              case 'currency':
                fieldType = ConditionsFieldsTypes.Number;
                break;
              default:
                fieldType = ConditionsFieldsTypes.String;
            }
            factsMap.set(criteria.name, { fact: criteria.name, type: fieldType, factType: criteria.name });

            // Collect string values from custom criteria
            if (fieldType === ConditionsFieldsTypes.String && criteria.value) {
              if (!stringOptionsMap.has(criteria.name)) {
                stringOptionsMap.set(criteria.name, new Set());
              }
              // Handle both single values and arrays
              const values = Array.isArray(criteria.value) ? criteria.value : [criteria.value];
              values.forEach((v: any) => {
                if (v && typeof v === 'string') {
                  stringOptionsMap.get(criteria.name)!.add(v);
                }
              });
            }
          });
        }
      });

      setListOfFacts(factsMap);

      // Convert string options map to SelectOption array map
      const selectOptionsMap = new Map<string, SelectOption[]>();
      stringOptionsMap.forEach((valuesSet, factName) => {
        const options = Array.from(valuesSet)
          .sort()
          .map(value => ({ value, label: value }));
        selectOptionsMap.set(factName, options);
      });
      setStringFactOptions(selectOptionsMap);

      setApplicantData(applicant);
    }
    // Only re-run when manualFields changes as well
  }, [isLoading, scorecardCriteria, manualFields, cs, creditApplication, equipmentData, vo]);

  useEffect(() => {
    if (results) {
      setTotalScore(results.totalScore || 0);
      setBandColor(results.bandColor || '#FFF');
      setBand(results.band || '');
      setBreakDown(results.breakdown);
    }
  }, [results]);

  const handleReRunScoreCard = async () => {
    if (!scorecardCriteria || !applicantData) return;

    let multiCriteriaLayers: Array<{ conditions: { all: Array<any> } }> = [];
    scorecardCriteria.jsonCriteria.scoreCriteria.map(criterias => {
      try {
        let customCriteria: any = [];
        let presetCriteria: any = [];
        if (!_.isEmpty(criterias.customCriteria)) {
          customCriteria = Conditions.transformRuleObject(criterias.customCriteria).conditions.all;
        }
        if (!_.isEmpty(criterias.jsonRules)) {
          presetCriteria = criterias.jsonRules.conditions.all.map(condition => {
            let value = condition.value;
            if (typeof value === 'string') {
              if (value.trim() !== '' && !isNaN(Number(value))) {
                value = Number(value);
              } else if (value === 'true' || value === 'false') {
                value = value === 'true';
              } else {
                value = value;
              }
            }

            return {
              ...condition,
              value,
            };
          });
        }
        // Ensure both are arrays before spreading
        multiCriteriaLayers.push({
          conditions: {
            all: [
              ...(Array.isArray(presetCriteria) ? presetCriteria : []),
              ...(Array.isArray(customCriteria) ? customCriteria : []),
            ],
          },
        });
      } catch (error) {
        console.error(error);
      }
    });

    // Merge manual fields into applicant data before transformation
    const mergedApplicant = { ...applicantData, ...manualFields };

    // Transform applicant data to handle SelectOption objects
    // Extract string values from SelectOption objects for scoring
    const transformedApplicant = Object.entries(mergedApplicant).reduce((acc, [key, value]) => {
      const factInfo = listOfFacts.get(key);

      if (factInfo?.type === ConditionsFieldsTypes.String) {
        // Handle SelectOption objects - extract the value property
        if (value && typeof value === 'object' && 'value' in value) {
          acc[key] = value.value;
        } else if (Array.isArray(value)) {
          // Legacy support: if it's an array, take the first element
          const firstValue = value[0];
          acc[key] =
            firstValue && typeof firstValue === 'object' && 'value' in firstValue ? firstValue.value : firstValue;
        } else {
          acc[key] = value;
        }
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const bands = scorecardCriteria.jsonCriteria.scoreTiers as ScoreBand[];
    const engine = new ScorecardEngine(multiCriteriaLayers, undefined, bands);
    setResults(await engine?.score(transformedApplicant));
  };

  const handleSaveScoreCard = async () => {
    // Prepare input for createCreditTiersResults mutation
    if (results) {
      const input = [
        {
          accountId: account?.id,
          vendorOpportunityId: vo?.vendorOpportunityId,
          processInstanceId: vo?.processInstanceId,
          prescreenCriteriaId: scorecardCriteria?.prescreenCriteriaId,
          creditSubmissionId: cs?.creditSubmissionId,
          financeProgramId,
          tiersType: 'SCORECARD',
          runtimeInput: applicantData,
          runtimeOutput: results,
          result: LenderWaterfallPrescreenCriteriaResultType.PASS,
          highestTierPassed: results.band || '',
          highestTierColor: results.bandColor || '',
        },
      ];
      setIsLoading(true);
      await handleCreateResults(input);
      if (refetchCreditSubmission) {
        await refetchCreditSubmission();
      }

      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (applicantData && financeProgramId) {
      handleReRunScoreCard();
    }
  }, [applicantData, financeProgramId, manualFields]);

  return scorecardCriteria ? (
    <Tile title="Scorecard" useCollapsing>
      <CardContainer
        title=""
        variant="outlined"
        contentViewType={contentViewType}
        actionBtnProps={
          !isLoading
            ? {
                label: 'Save',
                onClick: handleSaveScoreCard,
              }
            : undefined
        }
      >
        {!isLoading && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Scorecard results: {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        )}
        <CardContent style={{ padding: 0 }}>
          {isLoading ? (
            <LinearProgress style={{ width: '100%' }} />
          ) : (
            <>
              {/* Score summary section */}
              <Paper elevation={0} sx={{ bgcolor: 'grey.50', mx: 2, mt: 1, mb: 3, p: 3, borderRadius: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Total Score
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: bandColor,
                          width: 80,
                          height: 80,
                          fontSize: '2rem',
                          fontWeight: 'bold',
                          margin: '0 auto',
                          boxShadow: 2,
                        }}
                        variant="rounded"
                      >
                        {totalScore}
                      </Avatar>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Credit Band
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Chip
                          label={band || 'N/A'}
                          sx={{
                            bgcolor: bandColor,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '1rem',
                            py: 2.5,
                            px: 2,
                            height: 'auto',
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  {breakDown && breakDown.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                          Score Breakdown
                        </Typography>
                        <Stack spacing={0.5}>
                          {Object.values(
                            breakDown.reduce(
                              (acc, item) => {
                                acc[item.category] = item;
                                return acc;
                              },
                              {} as Record<string, ScoreBreakdownItem>
                            )
                          ).map(item => (
                            <Box
                              key={item.category}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 0.5,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <CheckCircleIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.813rem' }}>
                                  {item.category
                                    .replace(/_/g, ' ')
                                    .toLowerCase()
                                    .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </Typography>
                              </Box>
                              <Chip
                                label={`+${item.score}`}
                                size="small"
                                color="primary"
                                sx={{ minWidth: 50, fontWeight: 600 }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              <Divider sx={{ mb: 3 }} />

              {/* Input data section */}
              <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  Applicant Data
                </Typography>
                <Grid container spacing={2}>
                  {Array.from(listOfFacts.values()).map((factObj: any) => {
                    const key = factObj.fact;
                    const label = key
                      .replace(/_/g, ' ')
                      .toLowerCase()
                      .replace(/\b\w/g, (l: string) => l.toUpperCase());
                    let value = manualFields[key] ?? (applicantData ? (applicantData as any)[key] : '');

                    // Type-based rendering using ConditionsFieldsTypes enum
                    let inputElement = null;

                    switch (factObj.type) {
                      case ConditionsFieldsTypes.Boolean:
                        const booleanOptions: SelectOption[] = [
                          { value: 'true', label: 'Yes' },
                          { value: 'false', label: 'No' },
                        ];

                        const booleanValue = (() => {
                          if (value === true || value === 'true') return booleanOptions[0];
                          if (value === false || value === 'false') return booleanOptions[1];
                          return null;
                        })();

                        inputElement = (
                          <Autocomplete
                            size="small"
                            options={booleanOptions}
                            value={booleanValue}
                            onChange={(event, newValue) => {
                              if (newValue) {
                                setManualFields(f => ({ ...f, [key]: newValue.value === 'true' }));
                              } else {
                                setManualFields(f => ({ ...f, [key]: undefined }));
                              }
                            }}
                            getOptionLabel={option => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={params => <TextField {...params} variant="outlined" placeholder="Select..." />}
                          />
                        );
                        break;

                      case ConditionsFieldsTypes.Number:
                        // Check if this is a currency type based on field name patterns
                        const isCurrency =
                          key.includes('BALANCE') || key.includes('DOWN_PAYMENT') || key.includes('DEAL_SIZE');

                        inputElement = (
                          <TextField
                            size="small"
                            type="number"
                            value={value ?? ''}
                            onChange={e => {
                              const inputValue = e.target.value;
                              setManualFields(f => ({ ...f, [key]: inputValue === '' ? '' : Number(inputValue) }));
                            }}
                            variant="outlined"
                            fullWidth
                            placeholder={isCurrency ? '0.00' : '0'}
                            inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }}
                            InputProps={
                              isCurrency
                                ? {
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  }
                                : undefined
                            }
                          />
                        );
                        break;

                      case ConditionsFieldsTypes.String:
                        // Use Autocomplete for predefined options (single selection)
                        const autocompleteConfig = autocompletePropsByType[factObj.factType as FACT_NAMES_STRING];
                        // Get dynamically collected options for this fact
                        const dynamicOptions = stringFactOptions.get(key);

                        if (autocompleteConfig || dynamicOptions) {
                          // Prefer predefined options, fall back to dynamic options
                          const optionsToUse = autocompleteConfig?.options || dynamicOptions || [];

                          // Normalize value to SelectOption for single selection
                          const normalizedValue = (() => {
                            if (!value) return null;

                            // If it's already a SelectOption, use it
                            if (typeof value === 'object' && 'value' in value) {
                              return value;
                            }

                            // If it's a string, find the matching option
                            if (typeof value === 'string') {
                              const option = optionsToUse.find((opt: SelectOption) => opt.value === value);
                              return option || { value, label: value };
                            }

                            // If it's an array (from old data), take the first element
                            if (Array.isArray(value) && value.length > 0) {
                              const firstValue = value[0];
                              if (typeof firstValue === 'object' && 'value' in firstValue) {
                                return firstValue;
                              }
                              const option = optionsToUse.find((opt: SelectOption) => opt.value === firstValue);
                              return option || { value: firstValue, label: firstValue };
                            }

                            return null;
                          })();

                          inputElement = (
                            <Autocomplete
                              size="small"
                              options={optionsToUse}
                              value={normalizedValue}
                              onChange={(event, newValue) => {
                                // Store the SelectOption or null
                                setManualFields(f => ({ ...f, [key]: newValue }));
                              }}
                              getOptionLabel={option => {
                                if (typeof option === 'string') return option;
                                return option.label || option.value || '';
                              }}
                              isOptionEqualToValue={(option, value) => {
                                const optionValue = typeof option === 'object' ? option.value : option;
                                const valueValue = typeof value === 'object' ? value.value : value;
                                return optionValue === valueValue;
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  placeholder={
                                    typeof autocompleteConfig?.textFieldProps?.label === 'string'
                                      ? autocompleteConfig.textFieldProps.label
                                      : 'Select...'
                                  }
                                />
                              )}
                            />
                          );
                        } else {
                          // Free-form text input for strings without predefined options
                          inputElement = (
                            <TextField
                              size="small"
                              value={Array.isArray(value) ? value.join(', ') : (value ?? '')}
                              onChange={e => {
                                setManualFields(f => ({ ...f, [key]: e.target.value }));
                              }}
                              variant="outlined"
                              fullWidth
                              placeholder="Enter value..."
                            />
                          );
                        }
                        break;

                      default:
                        // Fallback to text input
                        inputElement = (
                          <TextField
                            size="small"
                            value={value ?? ''}
                            onChange={e => {
                              setManualFields(f => ({ ...f, [key]: e.target.value }));
                            }}
                            variant="outlined"
                            fullWidth
                            placeholder="Enter value..."
                          />
                        );
                    }

                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 0.5,
                              fontWeight: 500,
                              color: 'text.secondary',
                              fontSize: '0.813rem',
                            }}
                          >
                            {label}
                          </Typography>
                          {inputElement}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </>
          )}
        </CardContent>
      </CardContainer>
    </Tile>
  ) : null;
};

const RowNameText = ({ text }: { text: string }) => {
  const classes = useStyles();
  return <Typography className={classes.listRowName}>{text || ''}</Typography>;
};

const RowValueText = ({ text, typographyProps }: { text: string; typographyProps?: any }) => {
  const classes = useStyles();
  return (
    <Typography className={classes.listRowValue} {...typographyProps}>
      {text || ''}
    </Typography>
  );
};

const useStyles = makeStyles(theme => ({
  link: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  listRowName: {
    color: '#666666',
    fontWeight: 500,
  },
  listRowValue: {
    color: '#212121',
  },
  card: {
    margin: 0,
    height: '100%',
    width: '100%',

    '& .MuiCardHeader-action': {
      margin: 0,
    },
    '& .MuiCardContent-root': {
      paddingTop: 0,
    },
  },
  cardHeader: {
    margin: '10px 10px 0 10px',
  },
  cardHeaderDateOfApplication: {
    display: 'flex',
    justifyContent: 'flex-end',
    color: '#666666',
  },
  recerteficationErrorIcon: {
    /*     margin: "0 8px 3px 0", */
    color: 'red',
    fontSize: 22,
    marginLeft: 5,
  },
}));
